"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "@/lib/compat/i18n";
import clsx from "clsx";
import { PillarRing, type Pillar } from "@/components/pillars/PillarRing";
import { PerspectiveGlyph } from "./PerspectiveGlyph";
import type { EventLogState } from "@/lib/sse/useSSE";

/**
 * Top-of-frame heads-up-display. Pillar rings on the start side (always
 * visible per design principle 1), Orbitra Global wordmark in the middle, and a
 * perspective glyph + screen label on the end side.
 *
 * Anchor-pulse: when an event whose `eventType` matches an anchor pattern
 * lands in the SSE stream, the corresponding pillar pulses for ~700 ms.
 * Free credibility — every "this is on the ledger" moment gets a visible
 * cue instead of remaining a typographically-small `seq XX` line.
 */

/** eventType prefix → pillar that should pulse when it fires. */
const ANCHOR_EVENT_TO_PILLAR: Array<{ match: RegExp; pillar: Pillar }> = [
  // Verify pillar — onboarding, identity, KYB.
  { match: /^(identity\.OrbIDIssued|verification\.LiveSessionCompleted|onboarding\.AccountCreated)$/, pillar: "verify" },
  // Trace pillar — every document/anchor/regulator event lives here.
  { match: /^(document\.(Attested|BatchAttested|ConsistencyReportProduced|Validated)|insurance\.CertAnchored|compliance\.SnapshotProduced|logistics\.MilestoneRecorded)$/, pillar: "trace" },
  // Finance pillar — LC + settlement + reconciliation.
  { match: /^(lc\.(Issued|FundingMixSelected)|settlement\.EventReceived|reconciliation\.ThreeFieldMatched|tariff\.Settled)$/, pillar: "finance" },
];

function anchorPillarFor(eventType: string): Pillar | null {
  for (const e of ANCHOR_EVENT_TO_PILLAR) {
    if (e.match.test(eventType)) return e.pillar;
  }
  return null;
}

export function HUD({ ev, className }: { ev: EventLogState; className?: string }) {
  const t = useTranslations();
  const tHud = useTranslations("hud");

  // Track which pillar to pulse based on the most recently arrived
  // anchor event. `pulseKey` increments per anchor so back-to-back
  // anchors don't merge — the PillarRing resets its 700 ms timer on
  // every key change.
  const [pulse, setPulse] = useState<Pillar | null>(null);
  const [pulseKey, setPulseKey] = useState(0);
  const lastSeenIdRef = useRef<string | null>(null);

  useEffect(() => {
    const latest = ev.events[ev.events.length - 1];
    if (!latest) return;
    if (lastSeenIdRef.current === latest.eventId) return;
    lastSeenIdRef.current = latest.eventId;
    const pillar = anchorPillarFor(latest.eventType);
    if (pillar) {
      setPulse(pillar);
      setPulseKey((k) => k + 1);
    }
  }, [ev.events]);

  return (
    <header className={clsx("flex items-center gap-4", className)}>
      <PillarRing fill={ev.pillarFill} size={56} pulse={pulse} pulseKey={pulseKey} />
      <div className="flex flex-col">
        <p className="font-mono text-[9px] uppercase tracking-[0.32em] text-orbitra-teal-soft">Orbitra Global</p>
        <p className="font-display text-sm font-semibold tracking-tight text-orbitra-paper">
          {ev.currentAct ? `${tHud("actPrefix")} ${ev.currentAct.act} · ${tHud(`actLabel.${ev.currentAct.act}`, { fallback: ev.currentAct.label })}` : t("ui.confidential")}
        </p>
      </div>

      <div className="ms-auto flex flex-col items-end gap-1">
        {ev.currentScreen && <PerspectiveGlyph perspective={ev.currentScreen.perspective} />}
        {ev.currentScreen && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-orbitra-muted">
            {tHud("screenPrefix")} {ev.currentScreen.screen} · {tHud(`screenLabel.${ev.currentScreen.screen}`, { fallback: ev.currentScreen.label })}
          </p>
        )}
      </div>
    </header>
  );
}
